import React from 'react';
import { Dice1, X, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

/**
 * SecretDCQueue Component
 *
 * Displays the Secret DC queue with Quick Add grid and queued DCs.
 * Supports drag-to-reorder functionality with real-time RTDB sync.
 *
 * @param {Array} dcQueue - Array of DC objects
 * @param {Function} onQuickAdd - Handler for quick-adding a DC (1-30)
 * @param {Function} onUpdateDescription - Handler for updating DC description
 * @param {Function} onRemove - Handler for removing a DC from queue
 * @param {Function} onReorder - Handler for reordering the queue (receives new array)
 * @param {number} maxQueue - Maximum queue size (default: 5)
 */
const SecretDCQueue = ({
  dcQueue = [],
  onQuickAdd,
  onUpdateDescription,
  onRemove,
  onReorder,
  maxQueue = 5
}) => {
  // Handle drag end
  const handleDragEnd = (result) => {
    if (!result.destination) return; // Dropped outside the list

    const { source, destination } = result;

    // No movement
    if (source.index === destination.index) return;

    // Reorder the array
    const newQueue = Array.from(dcQueue);
    const [removed] = newQueue.splice(source.index, 1);
    newQueue.splice(destination.index, 0, removed);

    // Call parent handler to sync with RTDB
    onReorder(newQueue);
  };
  return (
    <div className="bg-gradient-to-br from-gray-900 to-black p-4 rounded-lg shadow-2xl border border-red-700">
      <h3 className="text-lg font-bold text-red-400 font-serif mb-4 flex items-center gap-2">
        <Dice1 className="w-5 h-5 text-red-400" />
        Secret DC Queue
        <span className="text-sm text-gray-500">({dcQueue.length}/{maxQueue})</span>
      </h3>

      {/* Quick Add Grid (1-30) - Always visible */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-400 mb-2">Quick Add</h4>
        <div className="grid grid-cols-6 gap-1.5">
          {Array.from({ length: 30 }, (_, i) => i + 1).map(num => (
            <button
              key={num}
              onClick={() => onQuickAdd(num)}
              disabled={dcQueue.length >= maxQueue}
              className={`h-9 rounded text-sm font-medium transition-all ${
                dcQueue.length >= maxQueue
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                  : 'bg-[#282828] text-gray-300 hover:bg-red-900/30 hover:text-red-400 hover:border-red-600 border border-gray-600'
              }`}
            >
              {num}
            </button>
          ))}
        </div>
      </div>

      {/* DC Queue */}
      <div>
        <h4 className="text-sm font-medium text-gray-400 mb-2">Queued DCs</h4>
        {dcQueue.length > 0 ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="dc-queue">
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-2"
                >
                  {dcQueue.map((dc, index) => (
                    <Draggable key={dc.id} draggableId={dc.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-start gap-2 p-2 rounded-lg border transition-all ${
                            snapshot.isDragging
                              ? 'bg-purple-900/40 border-purple-500 shadow-lg'
                              : dc.used
                              ? 'bg-gray-900/50 border-gray-700 opacity-50'
                              : index === 0
                              ? 'bg-purple-900/20 border-purple-600'
                              : 'bg-[#282828] border-gray-600'
                          }`}
                        >
                          {/* Drag Handle */}
                          <div
                            {...provided.dragHandleProps}
                            className="flex-shrink-0 text-gray-500 hover:text-red-400 transition-colors cursor-grab active:cursor-grabbing pt-1"
                          >
                            <GripVertical className="w-4 h-4" />
                          </div>

                          {/* DC Number */}
                          <div className="flex-shrink-0 flex flex-col items-center">
                            {index === 0 && !dc.used && (
                              <span className="text-[10px] text-purple-400 font-medium mb-0.5">NEXT</span>
                            )}
                            <span className={`text-xl font-bold ${
                              dc.used ? 'text-gray-600' : 'text-red-400'
                            }`}>
                              {dc.value}
                            </span>
                          </div>

                          {/* Description Input */}
                          <input
                            type="text"
                            value={dc.description || ''}
                            onChange={(e) => onUpdateDescription(dc.id, e.target.value)}
                            placeholder="Optional note..."
                            className="flex-1 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-sm text-gray-300 placeholder-gray-500 focus:outline-none focus:border-red-500"
                          />

                          {/* Remove Button */}
                          <button
                            onClick={() => onRemove(dc.id)}
                            className="flex-shrink-0 text-gray-500 hover:text-red-400 transition-colors"
                            aria-label="Remove DC"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div className="text-center py-8 text-gray-500 border border-gray-700 rounded-lg">
            <Dice1 className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No DCs queued</p>
            <p className="text-xs mt-1">Use Quick Add above ↑</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecretDCQueue;
